/**
 * Записать решение по нескольким дням календаря разом.
 *
 * `schedule` — «как по графику»: своих записей на этих датах не остаётся (записи
 * сверху не трогаются — их здесь не удалить, только перекрыть). Остальное — запись
 * этого уровня на каждую дату.
 *
 * По одному запросу на дату, по порядку: пакетного маршрута у сервера нет, а при
 * сбое на середине записанным должен оказаться предсказуемый префикс, а не случайный
 * набор.
 */
import {deleteCalendarException, fetchCalendar, putCalendarException} from './api';
import type {CalendarException, CalendarKind} from './types';

export type DayChoice = 'schedule' | CalendarKind;

/**
 * Что было на датах до записи — своя запись или ничего — и что записалось. Этого
 * хватает, чтобы вернуть всё как было (`undoDays`), не перечитывая календарь.
 */
export type Applied = {
  count: number;
  before: Map<string, CalendarException | null>;
  created: CalendarException[];
};

export async function applyDays(
  companyId: string,
  departmentId: string | undefined,
  dates: ReadonlyArray<string>,
  choice: DayChoice,
  title: string,
  /** Видимые записи: по ним находятся свои записи для «как по графику». */
  exceptions: ReadonlyArray<CalendarException>,
): Promise<Applied> {
  const own = departmentId ?? '';
  const before = new Map<string, CalendarException | null>();
  const created: CalendarException[] = [];
  for (const date of dates) {
    const mine = exceptions.filter(x => x.date === date && x.department_id === own);
    before.set(date, mine[0] ?? null);
    if (choice === 'schedule') {
      for (const x of mine) {
        await deleteCalendarException(companyId, x.id);
      }
    } else {
      created.push(
        await putCalendarException(companyId, {
          date,
          departmentId: departmentId ?? null,
          kind: choice,
          title: title.trim(),
        }),
      );
    }
  }
  return {count: dates.length, before, created};
}

/**
 * Вернуть даты к тому, что было до `applyDays`: прежняя своя запись — записать её
 * снова; не было — удалить ту, что появилась.
 */
export async function undoDays(
  companyId: string,
  departmentId: string | undefined,
  applied: Applied,
): Promise<void> {
  const own = departmentId ?? '';
  // Даты, где своей записи до нажатия не было: появившуюся надо удалить. Её id берётся
  // из ответа на запись, а если ответ его не принёс — из свежего чтения календаря,
  // чтобы «Отменить» не зависела от того, что именно вернул сервер.
  const toRemove: string[] = [];
  for (const [date, prev] of applied.before) {
    if (prev) {
      await putCalendarException(companyId, {
        date,
        departmentId: departmentId ?? null,
        kind: prev.kind,
        title: prev.title,
      });
    } else {
      toRemove.push(date);
    }
  }
  if (toRemove.length === 0) {
    return;
  }
  let ids = toRemove
    .map(date => applied.created.find(x => x.date === date)?.id)
    .filter((id): id is string => !!id);
  if (ids.length < toRemove.length) {
    const sorted = [...toRemove].sort();
    const fresh = await fetchCalendar(companyId, {
      from: sorted[0],
      to: sorted[sorted.length - 1],
      departmentId,
    });
    ids = fresh
      .filter(x => x.department_id === own && toRemove.includes(x.date))
      .map(x => x.id);
  }
  for (const id of ids) {
    await deleteCalendarException(companyId, id);
  }
}
